import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import {
  getClinicSettings,
  ClinicSettingsRequest,
  selectClinicSettings,
  selectClinicSettingsTotalCount,
  clinicSettingsStatus,
} from '../../../store/reducers/ClinicSettingsSlice';
import { useDebounce } from '../../shared/useDebounce';

const DEFAULT_PAGE_SIZE = 20;

export const useClinicSettings = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  // Восстановление состояния при возврате с детальной страницы
  const savedState = location.state as
    | { search?: string; pageIndex?: number; pageSize?: number }
    | undefined;

  // Флаг: восстановили из state один раз, далее не используем
  const restoredRef = useRef(false);
  const initialSearch =
    !restoredRef.current && savedState?.search ? savedState.search : '';
  const initialPageIndex =
    !restoredRef.current && savedState?.pageIndex ? savedState.pageIndex : 0;
  const initialPageSize =
    !restoredRef.current && savedState?.pageSize
      ? savedState.pageSize
      : DEFAULT_PAGE_SIZE;

  const [pageSize, setPageSize] = useState(initialPageSize);
  const [callRequest, setCallRequest] = useState<ClinicSettingsRequest>({
    ClinicNameSearch: initialSearch,
    PageIndex: initialPageIndex,
    PageSize: initialPageSize,
  });

  // Значение поля поиска (для controlled input)
  const [searchInputValue, setSearchInputValue] = useState(initialSearch);

  // Очищаем location.state после восстановления, чтобы при F5 не возвращалось старое
  useEffect(() => {
    if (!restoredRef.current && savedState) {
      restoredRef.current = true;
      // Заменяем state на пустой, чтобы при обновлении страницы не восстанавливался старый поиск
      navigate(location.pathname, { replace: true, state: undefined });
    }
  }, []);

  const items = useAppSelector(selectClinicSettings);
  const totalItemsCount = useAppSelector(selectClinicSettingsTotalCount);
  const status = useAppSelector(clinicSettingsStatus);

  const debouncedSearch = useDebounce(callRequest.ClinicNameSearch, 500);
  const prevSearchRef = useRef<string>('');

  useEffect(() => {
    const isSearchChanged = prevSearchRef.current !== debouncedSearch;
    prevSearchRef.current = debouncedSearch;

    if (isSearchChanged) {
      setCallRequest((prev) => ({ ...prev, PageIndex: 0 }));
    }

    dispatch(
      getClinicSettings({ ...callRequest, ClinicNameSearch: debouncedSearch }),
    );
  }, [callRequest.PageIndex, callRequest.PageSize, debouncedSearch]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInputValue(value);
    setCallRequest((prev) => ({
      ...prev,
      ClinicNameSearch: value,
      PageIndex: 0,
    }));
  };

  const handleChange = (page: number) => {
    setCallRequest((prev) => ({
      ...prev,
      PageIndex: page - 1,
    }));
  };

  const handlePageSizeChange = (value: string) => {
    const newSize = Number(value);
    setPageSize(newSize);
    setCallRequest((prev) => ({
      ...prev,
      PageSize: newSize,
      PageIndex: 0,
    }));
  };

  return {
    items,
    totalItemsCount,
    status,
    callRequest,
    pageSize,
    searchInputValue,
    handleSearch,
    handleChange,
    handlePageSizeChange,
  };
};
